import React, { useState } from "react";
import { Container, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography, CardMedia, Grid, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Switch, Paper } from "@mui/material";
import JSZip from "jszip";
import DataEditModal from "./Util/DataEditModal"
import mcmDroidsData from "./homebrew/mcmDroids.json"
import reSpecializedData from "./homebrew/reSpecialized.json"
import genesysVehiclesData from "./homebrew/genesysVehicles.json"
import { GENESYS_VEHICLES_PACK_ID, GENESYS_VEHICLE_OPTIONS, getGenesysOptions, setGenesysOption } from "./Util/GenesysVehicleRules"
import armor from "./data/armor.json"
import attachments from "./data/attachments.json"
import callToAdventure from "./data/callToAdventure.json"
import careers from "./data/careers.json"
import craftingResultTables from "./data/craftingResultTables.json"
import craftingTemplates from "./data/craftingTemplates.json"
import cultures from "./data/cultures.json"
import duties from "./data/duties.json"
import forceExperience from "./data/forceExperience.json"
import forceAbilities from "./data/forceAbilities.json"
import forcePowers from "./data/forcePowers.json"
import gear from "./data/gear.json"
import mentors from "./data/mentors.json"
import moralities from "./data/moralities.json"
import motivations from "./data/motivations.json"
import obligations from "./data/obligations.json"
import qualities from "./data/qualities.json"
import signatureAbilities from "./data/signatureAbilities.json"
import signatureAbilityNodes from "./data/signatureAbilityNodes.json"
import skills from "./data/skills.json"
import specializations from "./data/specializations.json"
import species from "./data/species.json"
import specificMotivations from "./data/specificMotivations.json"
import talents from "./data/talents.json"
import vehicles from "./data/vehicles.json"
import weapons from "./data/weapons.json"

const defaultDataMap = {
    armor,
    attachments,
    callToAdventure,
    careers,
    craftingResultTables,
    craftingTemplates,
    cultures,
    duties,
    forceAbilities,
    forceExperience,
    forcePowers,
    gear,
    mentors,
    moralities,
    motivations,
    obligations,
    qualities,
    signatureAbilities,
    signatureAbilityNodes,
    skills,
    specializations,
    species,
    specificMotivations,
    talents,
    vehicles,
    weapons
};

const HOMEBREW_PACKS = [
    {
        id: 'mcmDroids',
        name: mcmDroidsData.name,
        description: mcmDroidsData.description,
        speciesReplacements: mcmDroidsData.species,
        speciesKeys: mcmDroidsData.replaces.species,
    },
    {
        id: 'reSpecialized',
        name: reSpecializedData.name,
        description: reSpecializedData.description,
        specializationReplacements: reSpecializedData.specializations,
        specializationKeys: reSpecializedData.replaces.specializations,
        specializationRemoves: reSpecializedData.removes?.specializations,
        talentAdditions: reSpecializedData.talents,
        careerAdditions: reSpecializedData.careerAdditions,
        careerRemovals: reSpecializedData.careerRemovals,
        newCareers: reSpecializedData.newCareers,
    },
    {
        id: genesysVehiclesData.id,
        name: genesysVehiclesData.name,
        description: genesysVehiclesData.description,
    }
];

const HomebrewModal = ({ appFiles, setAppFiles }) => {
    const [open, setOpen] = useState(false);
    const [homebrewState, setHomebrewState] = useState(() =>
        JSON.parse(localStorage.getItem('homebrewState') || '{}')
    );
    const [genesysOptions, setGenesysOptions] = useState(() => getGenesysOptions());
    const [dirty, setDirty] = useState(false);

    const handleCloseModal = () => {
        setOpen(false);
        if (dirty) window.location.reload(false);
    };

    const handleSubToggle = (optionId, enable) => {
        setGenesysOption(optionId, enable);
        setGenesysOptions(getGenesysOptions());
        setDirty(true);
    };

    const handleToggle = (packId, enable) => {
        const pack = HOMEBREW_PACKS.find(p => p.id === packId);

        if (enable && pack.speciesKeys?.length) {
            const rawFiles = JSON.parse(localStorage.getItem('appFiles') || '{}');
            const storedSpecies = rawFiles.species ? JSON.parse(rawFiles.species) : null;
            if (storedSpecies) {
                const conflicts = pack.speciesKeys.filter(key => {
                    const defaultEntry = species.find(s => s.Key === key);
                    const userEntry = storedSpecies.find(s => s.Key === key);
                    return defaultEntry && userEntry && JSON.stringify(defaultEntry) !== JSON.stringify(userEntry);
                });
                if (conflicts.length > 0) {
                    const names = conflicts.map(k => storedSpecies.find(s => s.Key === k)?.Name ?? k).join(', ');
                    if (!window.confirm(`Warning: Your custom data for species "${names}" will be overridden while this homebrew is active. It will be restored when the pack is disabled. Proceed?`)) return;
                }
            }
        }

        const newState = { ...homebrewState, [packId]: enable };
        localStorage.setItem('homebrewState', JSON.stringify(newState));
        setHomebrewState(newState);
        setDirty(true);
    };

    return (
        <div>
            <Button variant="contained" onClick={() => setOpen(true)}>
                Toggle Homebrew
            </Button>
            <Dialog open={open} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                <DialogTitle>Homebrew</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Toggle homebrew supplements to add or remove custom content. Toggling a pack off restores the original data.
                    </DialogContentText>
                    {HOMEBREW_PACKS.map(pack => (
                        <React.Fragment key={pack.id}>
                            <Paper elevation={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, mb: 1 }}>
                                <Box sx={{ flex: 1, mr: 2 }}>
                                    <Typography variant="subtitle1"><b>{pack.name}</b></Typography>
                                    <Typography variant="body2" color="text.secondary">{pack.description}</Typography>
                                </Box>
                                <Switch
                                    checked={!!homebrewState[pack.id]}
                                    onChange={(e) => handleToggle(pack.id, e.target.checked)}
                                />
                            </Paper>
                            {pack.id === GENESYS_VEHICLES_PACK_ID && !!homebrewState[pack.id] && (
                                <Box sx={{ ml: 4, mb: 2 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                                        Individual changes (all on by default):
                                    </Typography>
                                    {GENESYS_VEHICLE_OPTIONS.map(option => (
                                        <Paper key={option.id} elevation={0} variant="outlined" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, mt: 0.5 }}>
                                            <Box sx={{ flex: 1, mr: 2 }}>
                                                <Typography variant="body2"><b>{option.label}</b></Typography>
                                                <Typography variant="caption" color="text.secondary">{option.description}</Typography>
                                            </Box>
                                            <Switch
                                                size="small"
                                                checked={!!genesysOptions[option.id]}
                                                onChange={(e) => handleSubToggle(option.id, e.target.checked)}
                                            />
                                        </Paper>
                                    ))}
                                </Box>
                            )}
                        </React.Fragment>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

const ReplaceDataModal = ({ appFiles, setAppFiles }) => {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [error, setError] = useState('');
    const [replaceMode, setReplaceMode] = useState('whole');

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            if (selectedFile.name.toLowerCase().endsWith('.zip')) {
                setFile(selectedFile);
                setError('');
            } else {
                setFile(null);
                setError('Please select a file with .zip extension.');
            }
        } else {
            setFile(null);
            setError('No file selected.');
        }
    };

    async function unpackZipInMemory(file) {
        const zip = new JSZip();
        try {
            const arrayBuffer = await file.arrayBuffer();
            const loadedZip = await zip.loadAsync(arrayBuffer, { checkCRC32: true });
            return loadedZip;
        } catch (error) {
            throw new Error(`Failed to unpack ZIP: ${error.message}`);
        }
    }

    const fileExists = (fileName) => {
        const validFiles = [
            'armor.json', 'attachments.json', 'callToAdventure.json', 'careers.json', 'craftingTemplates.json',
            'craftingResultTables.json', 'characterSheet.json', 'cultures.json', 'duties.json', 'forceAbilities.json',
            'forceExperience.json', 'forcePowers.json', 'gear.json', 'mentors.json', 'moralities.json', 'motivations.json',
            'obligations.json', 'qualities.json', 'signatureAbilities.json', 'signatureAbilityNodes.json', 'skills.json',
            'specializations.json', 'species.json', 'specificMotivations.json', 'talents.json', 'vehicles.json',
            'warriorRewards.json', 'weapons.json'
        ];

        return validFiles.includes(fileName);
    };

    function updateFile(path, content, setAppFiles) {
        const minifiedContent = JSON.stringify(content);
        const files = JSON.parse(localStorage.getItem('appFiles') || '{}');
        files[path] = minifiedContent;
        localStorage.setItem('appFiles', JSON.stringify(files));

        setAppFiles(prevFiles => ({
            ...prevFiles,
            [path]: content
        }));
    }


    function mergeKeyValueUpdates(existingFileContent, newFileContent) {
        const existingData = JSON.parse(existingFileContent);
        const newData = JSON.parse(newFileContent);

        if (!Array.isArray(existingData) || !Array.isArray(newData)) {
            return JSON.stringify(existingData);
        }

        const newDataMap = new Map(
            newData.map(item => [(item.Key || item.Name), item.Description])
        );

        const updatedData = existingData.map(item => {
            const identifier = item.Key || item.Name;
            if (newDataMap.has(identifier)) {
                return { ...item, Description: newDataMap.get(identifier) };
            }
            return item;
        });

        newData.forEach(newItem => {
            const identifier = newItem.Key || newItem.Name;
            if (!existingData.some(item => (item.Key || item.Name) === identifier)) {
                updatedData.push(newItem);
            }
        });

        return JSON.stringify(updatedData, null, 2);
    }



    async function compareAndReplaceFiles(zip, setAppFiles) {
        const filesToReplace = [];

        for (const [relativePath, zipEntry] of Object.entries(zip.files)) {
            if (!zipEntry.dir) {
                const fileName = relativePath.split('/').pop();
                if (fileExists(fileName)) {
                    filesToReplace.push(fileName);
                }
            }
        }

        let summary = 'The following changes will be made:\n\n';
        summary += filesToReplace.length > 0 ? 'Files to be replaced:\n' + filesToReplace.join('\n') + '\n\n' : 'No files to replace.\n\n';

        if (!confirm(summary + 'Do you want to proceed with these changes?')) {
            return 'Operation cancelled by user.';
        }

        for (const [relativePath, zipEntry] of Object.entries(zip.files)) {
            if (!zipEntry.dir) {
                try {
                    const content = await zipEntry.async('string');
                    const fileName = relativePath.split('/').pop();
                    const fileKey = fileName.slice(0, -5);
                    const existingContent = appFiles[fileKey];

                    if (replaceMode === 'keyValue') {
                        if (existingContent) {
                            const mergedContent = mergeKeyValueUpdates(existingContent, content);
                            updateFile(fileKey, JSON.parse(mergedContent), setAppFiles);
                        } else {
                            const baseData = JSON.stringify(defaultDataMap[fileKey]);
                            const mergedContent = mergeKeyValueUpdates(baseData, content);
                            updateFile(fileKey, JSON.parse(mergedContent), setAppFiles);
                        }
                    } else if (replaceMode === 'merge') {
                        const existingItems = existingContent ? JSON.parse(existingContent) : (defaultDataMap[fileKey] || []);
                        const newItems = JSON.parse(content);

                        const existingKeys = new Set(existingItems.map(item => item.Key));

                        const uniqueNewItems = newItems.filter(item => !existingKeys.has(item.Key));

                        const mergedItems = [...existingItems, ...uniqueNewItems];

                        updateFile(fileKey, mergedItems, setAppFiles);
                    } else {
                        updateFile(fileKey, JSON.parse(content), setAppFiles);
                    }
                } catch (error) {
                    console.error(`Error processing file ${relativePath}:`, error);
                }
            }
        }

        return 'Files updated successfully.';
    }


    const handleReplaceData = async () => {
        if (!file) {
            setError('Please select a ZIP file');
            return;
        }

        setError('');
        try {
            console.log("Starting to unpack ZIP");
            const zip = await unpackZipInMemory(file);
            console.log("ZIP unpacked successfully");
            const result = await compareAndReplaceFiles(zip, setAppFiles);
            alert(result);
            handleClose();
        } catch (error) {
            setError(`Error: ${error.message}`);
        }
        window.location.reload(false);
    };

    return (
        <div>
            <Button variant="contained" onClick={handleClickOpen}>
                Import Custom Data
            </Button>
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Replace Data</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Select a ZIP file containing the replacement data files. Choose whether to replace entire files or just update specific descriptions.
                    </DialogContentText>
                    <input
                        accept=".zip"
                        style={{ display: 'none' }}
                        id="raised-button-file"
                        type="file"
                        onChange={handleFileChange}
                    />
                    <label htmlFor="raised-button-file" sx={{ paddingTop: "15px" }}>
                        <Button variant="raised" component="span">
                            Choose ZIP File
                        </Button>
                    </label>
                    {file && <Typography>{file.name}</Typography>}
                    {error && <Typography color="error">{error}</Typography>}
                </DialogContent>
                <FormControl component="fieldset" sx={{ paddingLeft: "20px" }}>
                    <RadioGroup
                        value={replaceMode}
                        onChange={(e) => setReplaceMode(e.target.value)}
                    >
                        <FormControlLabel value="whole" control={<Radio />} label="Replace all data" />
                        <FormControlLabel value="keyValue" control={<Radio />} label="Replace only descriptions" />
                        <FormControlLabel value="merge" control={<Radio />} label="Merge datasets" />
                    </RadioGroup>
                </FormControl>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleReplaceData} disabled={!file}>
                        Replace
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

function App({ appFiles, setAppFiles, theme, darkTheme, handleThemeChange, colorblind, handleColorblindChange }) {
    const handleClearStorage = () => {
        localStorage.clear()
        setAppFiles({})
        window.location.reload(false);
    }
    const [editModalOpen, setEditModalOpen] = useState(false)

    let editFiles = { ...defaultDataMap }
    for (let o in Object.keys(editFiles)) {
        if (appFiles[Object.keys(editFiles)[o]]) {
            editFiles[Object.keys(editFiles)[o]] = appFiles[Object.keys(editFiles)[o]]
        } else {
            editFiles[Object.keys(editFiles)[o]] = JSON.stringify(editFiles[Object.keys(editFiles)[o]])
        }
    }

    const handleDataExport = async () => {
        const zip = new JSZip();

        Object.keys(appFiles).forEach((key) => {
            const jsonContent = appFiles[key];
            zip.file(`${key}.json`, jsonContent);
        });

        const zipBlob = await zip.generateAsync({ type: "blob" });

        const url = window.URL.createObjectURL(zipBlob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "LocalData.zip";
        await new Promise((resolve) => setTimeout(resolve, 100));
        link.click();

        window.URL.revokeObjectURL(url);
    };

    return (
        <Container
            style={{
                marginBottom: "16px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center"
            }}
        >
            <Typography variant="h5" gutterBottom>A Star Wars Roleplaying Character Generator</Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, marginBottom: 2 }}>
                <Button onClick={() => window.open('https://www.patreon.com/HyperdriveGenerator', '_blank', 'noopener,noreferrer')}>
                    Support on Patreon
                </Button>
                <Button onClick={() => window.open('https://discord.gg/GyXJZqeUC4', '_blank', 'noopener,noreferrer')}>
                    Join the Discord
                </Button>
            </Box>

            <CardMedia
                height="300"
                component="img"
                className="logoImage"
                image={theme == darkTheme ? `${process.env.PUBLIC_URL}/LOGO_TRANSPARENT_LIGHT.png` : `${process.env.PUBLIC_URL}/LOGO_TRANSPARENT_DARK.png`}
                title={'HyperdriveLogo'}
                sx={{ objectFit: "contain", marginBottom: 2 }}
            />

            <Grid container spacing={2} justifyContent="center">
                <Grid item>
                    <HomebrewModal appFiles={appFiles} setAppFiles={setAppFiles} />
                </Grid>
                <Grid item>
                    <ReplaceDataModal appFiles={appFiles} setAppFiles={setAppFiles} />
                </Grid>
                <Grid item>
                    <Button variant="contained" onClick={() => setEditModalOpen(true)}>Edit Local Data</Button>
                </Grid>
                <Grid item>
                    <Button variant="contained" onClick={() => handleDataExport()}>Export Local Data</Button>
                </Grid>
                <Grid item>
                    <Button variant="contained" onClick={() => handleClearStorage()}>Clear Local Data</Button>
                </Grid>
                <Grid item xs={12}>
                    <Paper elevation={2} sx={{ display: 'inline-flex', gap: 4, padding: '12px 24px', marginTop: 1 }}>
                        <FormControlLabel
                            control={<Switch checked={theme !== darkTheme} onChange={handleThemeChange} />}
                            label="Light Mode"
                        />
                        <FormControlLabel
                            control={<Switch checked={colorblind} onChange={(e) => handleColorblindChange(e.target.checked)} />}
                            label="Colorblind Mode"
                        />
                    </Paper>
                </Grid>
            </Grid>
            {editModalOpen && (
                <DataEditModal
                    open={editModalOpen}
                    handleClose={() => setEditModalOpen(false)}
                    dataMap={editFiles}
                    appFiles={appFiles}
                    setAppFiles={setAppFiles}
                />
            )}
        </Container>
    );
}

export default App;
